import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BlogLayout from "./BlogLayout";
import {
  CheckCircle2,
  XCircle,
  Minus,
  Store,
  Code,
  Sparkles,
  Clock,
  Wallet,
  Wrench,
  Users,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";

const HowToCreateOripaSitePage: React.FC = () => {
  // 3つの方法
  const methods = [
    {
      id: "ec-platform",
      icon: Store,
      name: "ECプラットフォーム",
      subtitle: "BASE、STORES等",
      description: "汎用ECサービスでショップを開設し、オリパを「商品」として販売する方法",
      initialCost: "0円",
      monthlyCost: "0円〜",
      salesFee: "3〜6.6%",
      setupTime: "即日",
      techSkill: "不要",
      pros: [
        "初期費用0円で始められる",
        "即日開設可能（審査なし）",
        "操作が簡単",
        "決済機能が標準搭載",
        "デザインテンプレートが豊富",
      ],
      cons: [
        "抽選演出ができない",
        "在庫・当選確率の自動計算ができない",
        "オリパ特有の機能がない",
        "販売手数料がやや高め",
        "差別化が難しい",
      ],
      bestFor: "まずは小規模で試してみたい方、抽選演出にこだわらない方",
      color: "blue",
    },
    {
      id: "custom-dev",
      icon: Code,
      name: "独自開発",
      subtitle: "自社開発 or 外注",
      description: "自分でWebサイトを開発するか、開発会社に依頼してオリパサイトを構築する方法",
      initialCost: "50〜300万円",
      monthlyCost: "1〜10万円",
      salesFee: "決済手数料のみ（3.6%程度）",
      setupTime: "1〜6ヶ月",
      techSkill: "必要",
      pros: [
        "完全カスタマイズ可能",
        "独自の抽選演出が実装できる",
        "ランニングコストを抑えられる",
        "他サイトとの差別化が容易",
        "機能追加が自由",
      ],
      cons: [
        "初期費用が高額",
        "開発期間が長い",
        "技術的知識が必要",
        "保守・運用の手間がかかる",
        "セキュリティ対策を自前で行う必要",
      ],
      bestFor: "技術力がある方、独自ブランドを重視する方、大規模運営を目指す方",
      color: "purple",
    },
    {
      id: "oripa-saas",
      icon: Sparkles,
      name: "オリパ特化SaaS",
      subtitle: "JoyBase等",
      description: "オリパ販売に特化したSaaSサービスを利用して、専用のオリパサイトを開設する方法",
      initialCost: "0円",
      monthlyCost: "0円〜",
      salesFee: "6.6%（カード3.6% + システム3%）",
      setupTime: "即日〜数日",
      techSkill: "不要",
      pros: [
        "オリパに必要な機能が揃っている",
        "抽選演出（ガチャ体験）が標準搭載",
        "在庫・当選確率の自動管理",
        "初期費用0円で始められる",
        "セキュリティ対策済み",
      ],
      cons: [
        "カスタマイズに制限がある",
        "月額費用・手数料が発生",
        "サービス提供元に依存",
        "選択肢がまだ少ない",
      ],
      bestFor: "オリパ販売に集中したい方、抽選演出を提供したい方、技術に詳しくない方",
      color: "yellow",
    },
  ];

  // 比較項目
  const comparisonItems = [
    { label: "初期費用", key: "initialCost" },
    { label: "月額費用", key: "monthlyCost" },
    { label: "販売手数料", key: "salesFee" },
    { label: "開設期間", key: "setupTime" },
    { label: "技術知識", key: "techSkill" },
  ];

  // 機能比較
  const featureComparison = [
    { feature: "抽選演出（ガチャ）", ec: false, custom: true, saas: true },
    { feature: "10連/100連ガチャ", ec: false, custom: true, saas: true },
    { feature: "ラストワン賞", ec: false, custom: true, saas: true },
    { feature: "在庫自動管理", ec: false, custom: true, saas: true },
    { feature: "当選確率自動計算", ec: false, custom: true, saas: true },
    { feature: "ポイント制度", ec: false, custom: true, saas: true },
    { feature: "発送まとめ機能", ec: false, custom: true, saas: true },
    { feature: "独自ドメイン", ec: true, custom: true, saas: true },
    { feature: "デザインカスタマイズ", ec: "partial", custom: true, saas: "partial" },
    { feature: "決済機能", ec: true, custom: "partial", saas: true },
  ];

  const FeatureIcon = ({ value }: { value: boolean | string }) => {
    if (value === true) {
      return <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />;
    } else if (value === false) {
      return <XCircle className="w-5 h-5 text-gray-300 mx-auto" />;
    } else {
      return <Minus className="w-5 h-5 text-yellow-500 mx-auto" />;
    }
  };

  return (
    <BlogLayout
      title="オリパの作り方｜サイト構築3つの方法を徹底比較"
      description="オリパの作り方を初心者向けに解説。ECプラットフォーム、独自開発、オリパ特化SaaSの3つの方法を費用・機能・難易度で比較します。"
      category="ガイド"
      publishedAt="2025年12月1日"
      breadcrumbs={[{ label: "オリパの作り方" }]}
    >
      <Helmet>
        <title>オリパの作り方｜サイト構築3つの方法を徹底比較【2025年最新】| JoyBase</title>
        <meta
          name="description"
          content="オリパの作り方を初心者向けに徹底解説。BASE等のECプラットフォーム、独自開発、オリパ特化SaaSの3つの方法を費用・機能・難易度で比較。あなたに最適なオリパサイト構築方法が見つかります。"
        />
        <meta
          name="keywords"
          content="オリパ 作り方,オリパの作り方,オリパサイト 作り方,オリパ 始め方,オリパ屋 始め方,オリパ BASE,オリパサイト 開発,オリパ SaaS,ネットオリパ 作り方,オンラインオリパ 作り方"
        />
        <meta property="og:title" content="オリパの作り方｜サイト構築3つの方法を徹底比較【2025年最新】" />
        <meta
          property="og:description"
          content="オリパの作り方を初心者向けに解説。ECプラットフォーム、独自開発、オリパ特化SaaSの3つの方法を比較。"
        />
        <meta property="og:url" content="https://joybase.jp/blog/how-to-create-oripa-site" />
        <meta property="og:type" content="article" />
        <link rel="canonical" href="https://joybase.jp/blog/how-to-create-oripa-site" />
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "オリパの作り方｜サイト構築3つの方法を徹底比較",
            "description": "オリパの作り方を初心者向けに徹底解説。3つの方法を費用・機能・難易度で比較",
            "author": {
              "@type": "Organization",
              "name": "JOYBASE"
            },
            "publisher": {
              "@type": "Organization",
              "name": "JOYBASE",
              "url": "https://www.joybase.jp/"
            },
            "datePublished": "2025-12-01",
            "dateModified": "2025-12-14",
            "mainEntityOfPage": "https://joybase.jp/blog/how-to-create-oripa-site"
          }
        `}
        </script>
      </Helmet>

      {/* 直接回答セクション（フィーチャードスニペット対策） */}
      <section className="mb-8">
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            オリパの作り方【結論】
          </h2>
          <p className="text-gray-700 mb-4">
            <strong>オリパ（オリジナルパック）の作り方は主に3つ</strong>あります。
            費用をかけずに始めたいなら<strong>ECプラットフォーム（BASE等）</strong>、
            完全オリジナルを作りたいなら<strong>独自開発</strong>、
            抽選演出（ガチャ）機能を使いたいなら<strong>オリパ特化SaaS（JoyBase等）</strong>がおすすめです。
          </p>
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <p className="font-bold text-blue-700">① ECプラットフォーム</p>
              <p className="text-gray-600">初期費用0円・即日開設</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <p className="font-bold text-purple-700">② 独自開発</p>
              <p className="text-gray-600">完全カスタマイズ可能</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <p className="font-bold text-yellow-700">③ オリパ特化SaaS</p>
              <p className="text-gray-600">ガチャ演出・10連対応</p>
            </div>
          </div>
        </div>
      </section>

      {/* 導入 */}
      <section className="mb-12">
        <p>
          オリパ（オリジナルパック）販売を始めたいけど、どうやってオリパを作ればいいかわからない。
          そんな方に向けて、<strong>オリパの作り方・サイト構築の3つの方法</strong>をフラットに解説します。
        </p>
        <p>
          それぞれにメリット・デメリットがあるので、自分の状況に合った方法を選んでください。
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
          <h3 className="font-bold text-gray-800 mb-3 text-base">オリパを作る3つの方法</h3>
          <ol className="text-gray-700 text-sm space-y-2 m-0 list-decimal pl-5">
            <li><strong>ECプラットフォーム</strong>（BASE、STORES等）を使う</li>
            <li><strong>独自開発</strong>で構築する</li>
            <li><strong>オリパ特化SaaS</strong>（JoyBase等）で構築する</li>
          </ol>
        </div>
      </section>

      {/* 3つの方法の詳細 */}
      <section className="mb-12">
        <h2>3つの方法を詳しく解説</h2>

        <div className="space-y-8 not-prose">
          {methods.map((method) => (
            <div
              key={method.id}
              className={`rounded-xl border-2 overflow-hidden ${
                method.color === "yellow"
                  ? "border-yellow-400 bg-yellow-50"
                  : method.color === "purple"
                  ? "border-purple-300 bg-purple-50"
                  : "border-blue-300 bg-blue-50"
              }`}
            >
              {/* ヘッダー */}
              <div
                className={`px-6 py-4 ${
                  method.color === "yellow"
                    ? "bg-yellow-400"
                    : method.color === "purple"
                    ? "bg-purple-400"
                    : "bg-blue-400"
                }`}
              >
                <div className="flex items-center gap-3">
                  <method.icon className="w-6 h-6 text-gray-900" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{method.name}</h3>
                    <p className="text-sm text-gray-700">{method.subtitle}</p>
                  </div>
                </div>
              </div>

              {/* 内容 */}
              <div className="p-6">
                <p className="text-gray-700 mb-6">{method.description}</p>

                {/* 基本情報 */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="text-center p-3 bg-white rounded-lg">
                    <Wallet className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                    <p className="text-xs text-gray-500">初期費用</p>
                    <p className="font-bold text-gray-900">{method.initialCost}</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <TrendingUp className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                    <p className="text-xs text-gray-500">月額費用</p>
                    <p className="font-bold text-gray-900">{method.monthlyCost}</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <Store className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                    <p className="text-xs text-gray-500">販売手数料</p>
                    <p className="font-bold text-gray-900">{method.salesFee}</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <Clock className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                    <p className="text-xs text-gray-500">開設期間</p>
                    <p className="font-bold text-gray-900">{method.setupTime}</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <Wrench className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                    <p className="text-xs text-gray-500">技術知識</p>
                    <p className="font-bold text-gray-900">{method.techSkill}</p>
                  </div>
                </div>

                {/* メリット・デメリット */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="text-sm font-bold text-green-700 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      メリット
                    </h4>
                    <ul className="space-y-2">
                      {method.pros.map((pro, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      デメリット
                    </h4>
                    <ul className="space-y-2">
                      {method.cons.map((con, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* おすすめ */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-600 flex items-start gap-2">
                    <Users className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong className="text-gray-900">こんな方におすすめ：</strong>
                      {method.bestFor}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 比較表 */}
      <section className="mb-12">
        <h2>基本情報の比較</h2>

        <div className="overflow-x-auto not-prose -mx-4 px-4">
          <table className="w-full border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  項目
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-blue-800 bg-blue-50">
                  ECプラットフォーム
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-purple-800 bg-purple-50">
                  独自開発
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-yellow-800 bg-yellow-50">
                  オリパ特化SaaS
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonItems.map((item) => (
                <tr key={item.key}>
                  <td className="border border-gray-300 px-4 py-3 text-sm font-medium text-gray-900">
                    {item.label}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-sm text-center text-gray-600">
                    {methods[0][item.key as keyof typeof methods[0]]}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-sm text-center text-gray-600">
                    {methods[1][item.key as keyof typeof methods[1]]}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-sm text-center text-gray-600">
                    {methods[2][item.key as keyof typeof methods[2]]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 機能比較 */}
      <section className="mb-12">
        <h2>機能の比較</h2>

        <p>
          オリパ販売に必要な機能が揃っているかどうかで比較します。
        </p>

        <div className="overflow-x-auto not-prose -mx-4 px-4">
          <table className="w-full border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  機能
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-blue-800 bg-blue-50">
                  ECプラットフォーム
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-purple-800 bg-purple-50">
                  独自開発
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-yellow-800 bg-yellow-50">
                  オリパ特化SaaS
                </th>
              </tr>
            </thead>
            <tbody>
              {featureComparison.map((item, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-4 py-3 text-sm font-medium text-gray-900">
                    {item.feature}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    <FeatureIcon value={item.ec} />
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    <FeatureIcon value={item.custom} />
                  </td>
                  <td className="border border-gray-300 px-4 py-3 bg-yellow-50">
                    <FeatureIcon value={item.saas} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          ※ <CheckCircle2 className="w-4 h-4 inline text-green-500" /> 対応 /
          <Minus className="w-4 h-4 inline text-yellow-500 mx-1" /> 一部対応・要追加開発 /
          <XCircle className="w-4 h-4 inline text-gray-300" /> 非対応
        </p>
      </section>

      {/* どの方法を選ぶべき？ */}
      <section className="mb-12">
        <h2>どの方法を選ぶべき？</h2>

        <div className="space-y-6 not-prose">
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
              <Store className="w-5 h-5" />
              まずは試したい → ECプラットフォーム
            </h3>
            <p className="text-blue-700 text-sm">
              オリパ販売が初めてで、まずは小規模に試してみたい方におすすめ。
              BASEやSTORESなら今日から始められます。
              ただし、抽選演出（ガチャ体験）は提供できないため、
              「商品としてパックを販売する」形になります。
            </p>
          </div>

          <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
            <h3 className="font-bold text-purple-800 mb-2 flex items-center gap-2">
              <Code className="w-5 h-5" />
              完全オリジナルを作りたい → 独自開発
            </h3>
            <p className="text-purple-700 text-sm">
              独自のブランドを構築し、完全にカスタマイズされた体験を提供したい方向け。
              開発費用と時間を確保できるなら最も自由度が高い選択肢です。
              技術力があるか、信頼できる開発パートナーがいる場合に検討しましょう。
            </p>
          </div>

          <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
            <h3 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              本格的に・手軽に始めたい → オリパ特化SaaS
            </h3>
            <p className="text-yellow-700 text-sm">
              抽選演出などオリパに必要な機能を使いたいが、開発に時間やお金をかけたくない方におすすめ。
              JoyBaseなら初期費用0円で、10連ガチャやラストワン賞など本格的な機能が使えます。
            </p>
          </div>
        </div>
      </section>

      {/* 始める前に必要なこと */}
      <section className="mb-12">
        <h2>始める前に必要なこと</h2>

        <p>どの方法を選んでも、オリパ販売を始める前に以下の準備が必要です。</p>

        <div className="bg-gray-50 rounded-lg p-6 not-prose">
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                1
              </span>
              <div>
                <h4 className="font-bold text-gray-900">古物商許可証の取得</h4>
                <p className="text-sm text-gray-600 mt-1">
                  中古カードを扱う場合は必須。取得には約40日かかります。
                  <Link to="/blog/kobutsusho-guide" className="text-blue-600 hover:underline ml-1">
                    取得方法の詳細はこちら →
                  </Link>
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                2
              </span>
              <div>
                <h4 className="font-bold text-gray-900">仕入れルートの確保</h4>
                <p className="text-sm text-gray-600 mt-1">
                  カードショップ、オークション、個人買取など、安定した仕入れ先を確保しましょう。
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                3
              </span>
              <div>
                <h4 className="font-bold text-gray-900">銀行口座</h4>
                <p className="text-sm text-gray-600 mt-1">
                  売上受取用の口座が必要です。事業用に分けると管理しやすくなります。
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                4
              </span>
              <div>
                <h4 className="font-bold text-gray-900">オリパのコンセプト設計</h4>
                <p className="text-sm text-gray-600 mt-1">
                  ターゲット、価格帯、特徴など、自分のオリパの強みを明確にしましょう。
                </p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* まとめ */}
      <section>
        <h2>まとめ：オリパサイト構築方法の選び方</h2>

        <p>
          オリパサイトを構築する方法は主に3つ。それぞれに特徴があります。
        </p>

        <ul>
          <li><strong>手軽さ重視</strong> → ECプラットフォーム（BASE等）</li>
          <li><strong>自由度重視</strong> → 独自開発で構築</li>
          <li><strong>オリパ機能重視</strong> → オリパ特化SaaS（JoyBase等）で構築</li>
        </ul>

        <p>
          自分の予算、技術力、やりたいことに合わせて最適な構築方法を選んでください。
        </p>

        <p>
          もし「本格的なオリパサイトを手軽に構築したい」という方は、
          JoyBaseを検討してみてください。初期費用0円で、抽選演出・10連ガチャ・ラストワン賞など
          オリパ販売に必要な機能が揃っており、すぐにオリパサイトを構築できます。
        </p>
      </section>
    </BlogLayout>
  );
};

export default HowToCreateOripaSitePage;
